import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { CustomDialog } from '../../../components/StyledComponents';
import { DialogTitle, DialogContent } from '@mui/material';
import { GrayIconButton } from '../../../components/StyledButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../../context/AuthContext';
import { format, parse, isValid } from 'date-fns';
import CorfirmApproveRequestDialog from './CorfirmApproveRequestDialog';
import CorfirmRejectRequestDialog from './CorfirmRejectRequestDialog';
import { getLeaveByUserByLeaveId, getUserList } from '../../../axios/User/UserApi';
import LeaveApi from '../../../axios/LeaveAndHoliday/LeaveApi';
import { SpinnerRing } from '../../../components/CustomSpinner';
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarV2Icon16pxSvg from "../../../assets/images/icons/CalendarV2Icon16pxSvg";
import DefaultTooltip, { LeaveUsedDaysTooltipContent } from "../../../components/DefaultTooltip";
import QuestionIcon16pxSvg from "../../../assets/images/icons/QuestionIcon16pxSvg";

const formatDecimalNumber = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return value ?? 0;
    if (Number.isInteger(numberValue)) return numberValue;
    return Number(numberValue.toFixed(2));
};

const DEFAULT_FULL_DAY_MINUTES = 8 * 60;

const parseTimeStringToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const parts = String(timeStr).split(":").map(Number);
    const [h = 0, m = 0, s = 0] = parts;
    return h * 60 + m + Math.floor(s / 60);
};

const normalizeHoursPerLeaveDay = (value) => {
    if (!value) return "08:00:00";
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed || "08:00:00";
    }
    return "08:00:00";
};

const minutesToHhMmSs = (totalMinutes) => {
    const mins = Math.max(0, Math.round(Number(totalMinutes) || 0));
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
};

/** Làm tròn 2 chữ số thập phân */
const formatOneDecimal = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return 0;
    const rounded = Math.round(numberValue * 100) / 100;
    return Number.isInteger(rounded) ? rounded : rounded;
};

/**
 * usedFromYear (HH:mm:ss hoặc số ngày legacy) / hoursPerLeaveDay → số ngày
 */
const parseUsedFromYearToDays = (usedFromYear, hoursPerLeaveDay) => {
    if (usedFromYear == null || usedFromYear === "") return 0;

    const asString = String(usedFromYear).trim();
    if (asString.includes(":")) {
        const timeMins = parseTimeStringToMinutes(asString);
        const dayMins =
            parseTimeStringToMinutes(hoursPerLeaveDay) || DEFAULT_FULL_DAY_MINUTES;
        if (dayMins <= 0) return 0;
        return timeMins / dayMins;
    }

    const asNumber = Number(usedFromYear);
    return Number.isFinite(asNumber) ? asNumber : 0;
};

const resolveItemHoursPerLeaveDay = (item) =>
    normalizeHoursPerLeaveDay(
        item?.hoursPerLeaveDaySnapshot || item?.hoursPerLeaveDay || "08:00:00"
    );

const toLeaveBalanceItems = (leaveBalance) => {
    if (!leaveBalance) return [];
    return Array.isArray(leaveBalance) ? leaveBalance.filter(Boolean) : [leaveBalance];
};

const sumUsedFromYearDays = (items) =>
    toLeaveBalanceItems(items).reduce(
        (sum, item) =>
            sum +
            parseUsedFromYearToDays(item.usedFromYear, resolveItemHoursPerLeaveDay(item)),
        0
    );

const buildUsedFromYearDetails = (items) =>
    toLeaveBalanceItems(items).map((item) => {
        const hoursPerDay = resolveItemHoursPerLeaveDay(item);
        const usedRaw = item.usedFromYear;
        const usedTime =
            typeof usedRaw === "string" && String(usedRaw).includes(":")
                ? normalizeHoursPerLeaveDay(usedRaw)
                : minutesToHhMmSs(
                      parseUsedFromYearToDays(usedRaw, hoursPerDay) *
                          (parseTimeStringToMinutes(hoursPerDay) || DEFAULT_FULL_DAY_MINUTES)
                  );
        return {
            usedTime,
            hoursPerDay,
            days: formatOneDecimal(parseUsedFromYearToDays(usedRaw, hoursPerDay)),
        };
    });

/** Ưu tiên numOfTime; fallback numOfDay (dữ liệu cũ) → số ngày hiển thị */
const resolveDisplayDays = (data) => {
    if (!data) return null;
    if (data.numOfTime) {
        const timeMins = parseTimeStringToMinutes(data.numOfTime);
        const dayMins =
            parseTimeStringToMinutes(data.hoursPerLeaveDaySnapshot) ||
            DEFAULT_FULL_DAY_MINUTES;
        if (dayMins <= 0) return 0;
        return formatDecimalNumber(timeMins / dayMins);
    }
    if (data.numOfDay != null && data.numOfDay !== "") {
        return formatDecimalNumber(data.numOfDay);
    }
    return null;
};

const buildLeaveEntitlementInfo = (source) => {
    const items = toLeaveBalanceItems(source).filter(
        (item) => item.usedFromYear != null && item.totalEntitlementYear != null
    );
    if (!items.length) return null;

    const details = buildUsedFromYearDetails(items);
    const usedDays = formatOneDecimal(sumUsedFromYearDays(items));
    const totalDays = formatDecimalNumber(items[0].totalEntitlementYear);

    return {
        text: `${usedDays}/${totalDays}`,
        usedTime: details[0]?.usedTime || null,
        hoursPerDay: details[0]?.hoursPerDay || null,
        usedDays,
        details,
    };
};

const DetailRequestDialogAR = ({ onClose, leaveInfo, onRefresh, leaveTypesData, isRequestMode = false }) => {
    const { t } = useTranslation()
    const { dateTimeFormat, isAdmin } = useAuth();
    const { getLeaveById, getLeaveRequestId, getLeaveRequestIdHistory } = LeaveApi();
    const [openCorfirmApproveRequest, setOpenCorfirmApproveRequest] = useState(false);
    const [openCorfirmRejectRequest, setOpenCorfirmRejectRequest] = useState(false);
    const [activeTab, setActiveTab] = useState("details");
    const [loadingCount, setLoadingCount] = useState(0);
    const [requestHistory, setRequestHistory] = useState([]);
    const isLoading = loadingCount > 0;
    const toDateForPicker = (value, format) => {
        if (!value) return null;

        // 1. Đã là Date
        if (value instanceof Date && isValid(value)) {
            return value;
        }

        if (typeof value === "string") {
            const clean = value.replace(/\u00A0/g, " ").trim();

            // 2. ISO string → new Date()
            if (/^\d{4}-\d{2}-\d{2}T/.test(clean)) {
                const isoDate = new Date(clean);
                return isValid(isoDate) ? isoDate : null;
            }

            // 3. Đúng format setting → parse theo setting
            const parsed = parse(clean, format, new Date());
            if (isValid(parsed)) return parsed;
        }

        return null;
    };

    const normalizeDateFnsFormat = (fmt) => {
        return fmt
            // YEAR
            .replace(/YYYY/g, "yyyy")
            .replace(/YY/g, "yy")

            // DAY
            .replace(/DD/g, "dd")

            // MINUTE (fix sm ❌)
            .replace(/sm/g, "mm")
            .replace(/MM/g, "MM");
    };

    const safeDateTimeFormat = normalizeDateFnsFormat(dateTimeFormat);

    const formatRequestDateTime = (value) => {
        if (!value) return "-";
        const parsedDate = value instanceof Date ? value : toDateForPicker(value, safeDateTimeFormat);
        if (!parsedDate || !isValid(parsedDate)) return "-";
        return format(parsedDate, safeDateTimeFormat);
    };

    const getLeaveTypeLabel = (leaveTypeId) => {
        const leaveType = leaveTypesData?.find(
            (item) => String(item.leaveTypeId) === String(leaveTypeId)
        );
        if (!leaveType) return "-";

        const langId = Number(localStorage.getItem("i18nextLngId"));
        if (langId === 2) return leaveType.remarkEN || leaveType.remark || leaveType.leaveTypeName || "-";
        return leaveType.remark || leaveType.remarkEN || leaveType.leaveTypeName || "-";
    };

    const getRequestStatusLabel = (statusId) => {
        if (Number(statusId) === 1) return t("common.pending-request");
        if (Number(statusId) === 2) return t("common.approved");
        if (Number(statusId) === 3) return t("common.rejected");
        return "-";
    };

    const getRequestStatusColor = (statusId) => {
        if (Number(statusId) === 1) return "#F97316";
        if (Number(statusId) === 2) return "#16A34A";
        if (Number(statusId) === 3) return "#DC2626";
        return "#525252";
    };

    const parsePayloadData = (value) => {
        if (!value) return {};
        if (typeof value === "string") {
            try {
                return JSON.parse(value) || {};
            } catch {
                return {};
            }
        }
        return value;
    };

    const [allUser, setAllUser] = useState([]);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                setLoadingCount((prev) => prev + 1);
                const payload = {
                    statusId: 1,
                    page: 1,
                    pageSize: 1000000,
                    status: null,
                    langId: localStorage.getItem("i18nextLngId")
                }
                const response = await getUserList(payload);
                const usersList = response.data.data;
                setAllUser(usersList);
            } catch (error) {
                console.log(error)
            } finally {
                setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
        };
        fetchUser();

    }, []);


    const getFullNameByEmail = useCallback((email) => {
        if (!email) return "-";

        const normalizedEmail = String(email).trim().toLowerCase();

        const user = allUser.find(
            (u) =>
                u.email &&
                u.email.trim().toLowerCase() === normalizedEmail
        );

        // nếu tìm được thì trả fullName, không thì fallback về email
        return user?.fullName || email;
    }, [allUser]);

    const [formData, setFormData] = useState(null);
    const [leaveDetail, setLeaveDetail] = useState(null);
    const [leaveByUserByLeave, setLeaveByUserByLeaveData] = useState([]);

    useEffect(() => {
        if (!leaveInfo) return;

        const fillFromRowData = () => {
            const approvedByName = getFullNameByEmail(leaveInfo.modifiedBy);

            setLeaveDetail(leaveInfo);
            setFormData({
                userId: leaveInfo.userId,
                selectUser: leaveInfo.fullName || leaveInfo.name,
                requestedFrom: toDateForPicker(leaveInfo.requestStartDate, safeDateTimeFormat),
                requestedTo: toDateForPicker(leaveInfo.requestEndDate, safeDateTimeFormat),
                approvedFrom: toDateForPicker(leaveInfo.requestStartDate, safeDateTimeFormat),
                approvedTo: toDateForPicker(leaveInfo.requestEndDate, safeDateTimeFormat),
                leaveType: leaveInfo.leaveTypeId,
                leavePolicy: leaveInfo.leavePolicyId,
                requestReason: leaveInfo.requestReason,
                approveReason: leaveInfo.responseReason,
                selecStatusLeave: leaveInfo.approvalStatusId,
                modifiedBy: approvedByName,
                createdTime: leaveInfo.createdTime,
                displayDays: resolveDisplayDays(leaveInfo),
            });
        };

        if (isRequestMode) {
            const fetchLeaveRequestDetail = async () => {
                try {
                    setLoadingCount((prev) => prev + 1);
                    const response = await getLeaveRequestId({
                        leaveRequestId: leaveInfo.leaveRequestId ?? leaveInfo.leaveId,
                        langId: localStorage.getItem("i18nextLngId"),
                    });

                    const data = response?.data?.data?.[0];
                    if (!data) {
                        fillFromRowData();
                        return;
                    }

                    const newData = parsePayloadData(data.newData);
                    const oldData = parsePayloadData(data.oldData);
                    const requestType = String(data.requestType || "").toUpperCase();
                    const displayData = requestType === "DELETE_LEAVE" ? oldData : newData;
                    const approvedByName = getFullNameByEmail(data.modifiedBy);
                    setLeaveDetail({ ...data, newData, oldData });
                    setFormData({
                        userId: data.userId ?? displayData.userId,
                        selectUser: data.fullName || data.name,
                        requestedFrom: toDateForPicker(displayData.start, safeDateTimeFormat),
                        requestedTo: toDateForPicker(displayData.end, safeDateTimeFormat),
                        approvedFrom: toDateForPicker(displayData.start, safeDateTimeFormat),
                        approvedTo: toDateForPicker(displayData.end, safeDateTimeFormat),
                        leaveType: displayData.leaveTypeId,
                        leavePolicy: displayData.leavePolicyId,
                        requestReason: data.requestReason || displayData.requestReason,
                        approveReason: data.responseReason || displayData.responseReason,
                        selecStatusLeave: data.requestStatusId,
                        modifiedBy: approvedByName,
                        createdTime: data.createdTime,
                        displayDays: resolveDisplayDays(displayData),
                    });
                } catch (error) {
                    console.error("getLeaveRequestId error:", error);
                    fillFromRowData();
                } finally {
                    setLoadingCount((prev) => Math.max(prev - 1, 0));
                }
            };

            fetchLeaveRequestDetail();
            return;
        }

        if (!leaveInfo.leaveId) {
            fillFromRowData();
            return;
        }

        const fetchLeaveDetail = async () => {
            try {
                setLoadingCount((prev) => prev + 1);
                const response = await getLeaveById({
                    leaveId: leaveInfo.leaveId,
                    langId: localStorage.getItem("i18nextLngId"),
                });

                const data = response?.data?.data?.[0];
                if (!data) {
                    fillFromRowData();
                    return;
                }

                const approvedByName = getFullNameByEmail(data.modifiedBy);
                setLeaveDetail(data);
                setFormData({
                    userId: data.userId,
                    selectUser: data.fullName || data.name,
                    requestedFrom: toDateForPicker(data.requestStartDate, safeDateTimeFormat),
                    requestedTo: toDateForPicker(data.requestEndDate, safeDateTimeFormat),
                    approvedFrom: toDateForPicker(data.approveStartDate || data.requestStartDate, safeDateTimeFormat),
                    approvedTo: toDateForPicker(data.approveEndDate || data.requestEndDate, safeDateTimeFormat),
                    leaveType: data.leaveTypeId,
                    leavePolicy: data.leavePolicyId,
                    requestReason: data.requestReason,
                    approveReason: data.responseReason,
                    selecStatusLeave: data.approvalStatusId,
                    modifiedBy: approvedByName,
                    createdTime: data.createdTime,
                    displayDays: resolveDisplayDays(data),
                });
            } catch (error) {
                console.error("getLeaveById error:", error);
                fillFromRowData();
            } finally {
                setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
        };

        fetchLeaveDetail();
    }, [leaveInfo, allUser, safeDateTimeFormat, getFullNameByEmail, isRequestMode]);

    const fetchLeaveByUserByLeaveId = useCallback(
        async (userId, leaveTypeId) => {
            if (!userId || !leaveTypeId) return;

            const payload = {
                langId: localStorage.getItem("i18nextLngId"),
                userId,
                leaveTypeId,
                year: new Date().getFullYear(),
            };

            try {
                setLoadingCount((prev) => prev + 1);
                const response = await getLeaveByUserByLeaveId(payload);
                if (
                    response?.data?.message === "Successful" ||
                    response?.data?.message === "Thành công"
                ) {
                    setLeaveByUserByLeaveData(response.data.data ?? []);
                }
            } catch (error) {
                console.error("getLeaveByUserByLeaveId error:", error);
            } finally {
                setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
        },
        []
    );

    useEffect(() => {
        if (!formData?.userId || !formData?.leaveType) return;
        fetchLeaveByUserByLeaveId(formData.userId, formData.leaveType);
    }, [formData?.userId, formData?.leaveType, fetchLeaveByUserByLeaveId]);

    useEffect(() => {
        const leaveRequestId = leaveInfo?.leaveRequestId ?? leaveInfo?.leaveId;
        if (!isRequestMode || !leaveRequestId) {
            setRequestHistory([]);
            return;
        }

        const fetchHistory = async () => {
            try {
                setLoadingCount((prev) => prev + 1);
                const response = await getLeaveRequestIdHistory({
                    leaveRequestId,
                    langId: localStorage.getItem("i18nextLngId"),
                });
                const historyData = response?.data?.data;
                setRequestHistory(Array.isArray(historyData) ? historyData : []);
            } catch (error) {
                console.error("getLeaveRequestIdHistory error:", error);
                setRequestHistory([]);
            } finally {
                setLoadingCount((prev) => Math.max(prev - 1, 0));
            }
        };

        fetchHistory();
    }, [isRequestMode, leaveInfo?.leaveRequestId, leaveInfo?.leaveId]);

    const getLeaveHistoryActionLabel = useCallback((requestStatusName) => {
        const status = String(requestStatusName || "").toUpperCase();
        if (status === "APPROVED") return t("common.leave-history-action-approved");
        if (status === "REJECTED") return t("common.leave-history-action-rejected");
        return t("common.leave-history-action-pending");
    }, [t]);

    const getLeaveHistoryTypeLabel = useCallback((requestType) => {
        const type = String(requestType || "").toUpperCase();
        if (type === "EDIT_LEAVE") return t("common.leave-history-type-edit");
        if (type === "DELETE_LEAVE") return t("common.leave-history-type-delete");
        return t("common.leave-history-type-add");
    }, [t]);

    const parsedLeaveHistory = useMemo(() => {
        return (requestHistory || []).map((item) => {
            const newData = parsePayloadData(item?.newData);
            const oldData = parsePayloadData(item?.oldData);
            const requestType = String(item?.requestTypeName || "").toUpperCase();
            const displayData = requestType === "DELETE_LEAVE" ? oldData : newData;
            const requestStatusName = String(
                item?.requestStatusName || item?.requestStatus || ""
            ).toUpperCase();
            const actorEmail = item?.modifiedBy || item?.modifyBy || item?.createdBy;
            const actorName = item?.fullName || getFullNameByEmail(actorEmail);

            return {
                ...item,
                requestType,
                requestStatusName,
                actorName,
                createdAt: item?.createdAt || item?.createdTime || null,
                timeRangeLabel: `${formatRequestDateTime(displayData.start)} - ${formatRequestDateTime(displayData.end)}`,
                leaveTypeLabel: getLeaveTypeLabel(displayData.leaveTypeId ?? item?.leaveTypeId),
                reasonLabel:
                    requestStatusName === "PENDING"
                        ? (item?.requestReason || displayData?.requestReason || "-")
                        : (item?.responseReason || displayData?.responseReason || item?.requestReason || "-"),
                actionLabel: getLeaveHistoryActionLabel(requestStatusName),
                typeLabel: getLeaveHistoryTypeLabel(requestType),
            };
        });
    }, [
        requestHistory,
        getFullNameByEmail,
        getLeaveHistoryActionLabel,
        getLeaveHistoryTypeLabel,
        leaveTypesData,
        safeDateTimeFormat,
        t,
    ]);

    const handleCloseDialog = () => {
        onClose();
    };

    const handleCloseApproveRequest = () => {
        setOpenCorfirmApproveRequest(false)
    }

    const handleCloseRejectRequest = () => {
        setOpenCorfirmRejectRequest(false)
    }


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    }

    const [, setCheckRequestedTo] = useState(false)
    const [, setCheckRequestedFrom] = useState(false)

    useEffect(() => {
        if (!formData) return;

        setCheckRequestedFrom(
            formData.requestedFrom instanceof Date
                ? formData.requestedFrom.getTime()
                : null
        );

        setCheckRequestedTo(
            formData.requestedTo instanceof Date
                ? formData.requestedTo.getTime()
                : null
        );
    }, [formData]);

    const resolveHoursPerLeaveDaySnapshot = useCallback(
        (leaveTypeId) => {
            const fromTypes = (leaveTypesData || []).find(
                (item) => Number(item.leaveTypeId) === Number(leaveTypeId)
            );
            const fromBalance = (leaveByUserByLeave || []).find(
                (item) => Number(item.leaveTypeId) === Number(leaveTypeId)
            ) || leaveByUserByLeave?.[0];
            return normalizeHoursPerLeaveDay(
                fromTypes?.hoursPerLeaveDay ??
                    fromBalance?.hoursPerLeaveDay ??
                    fromBalance?.hoursPerLeaveDaySnapshot ??
                    leaveDetail?.hoursPerLeaveDay ??
                    leaveDetail?.hoursPerLeaveDaySnapshot ??
                    leaveInfo?.hoursPerLeaveDay ??
                    leaveInfo?.hoursPerLeaveDaySnapshot ??
                    "08:00:00"
            );
        },
        [leaveTypesData, leaveByUserByLeave, leaveDetail, leaveInfo]
    );

    /** SUM(usedFromYear / snapshot) khi cùng leaveTypeId */
    const leaveEntitlementInfo = useMemo(() => {
        if (leaveByUserByLeave?.length) {
            return (
                buildLeaveEntitlementInfo(leaveByUserByLeave) || {
                    text: "-",
                    usedTime: null,
                    hoursPerDay: null,
                    usedDays: null,
                    details: [],
                }
            );
        }

        const fromDetail = buildLeaveEntitlementInfo(leaveDetail);
        if (fromDetail) return fromDetail;

        const fromInfo = buildLeaveEntitlementInfo(leaveInfo);
        if (fromInfo) return fromInfo;

        return {
            text: leaveDetail?.leaveEntitlement || leaveInfo.leaveEntitlement || "-",
            usedTime: null,
            hoursPerDay: null,
            usedDays: null,
            details: [],
        };
    }, [leaveByUserByLeave, leaveDetail, leaveInfo]);

    if (!formData) {
        return (
            <CustomDialog maxWidth={'md'} open onClose={undefined} keepMounted>
                <div className="flex items-center justify-center min-h-[200px]">
                    <SpinnerRing loading size={50} color="#3B82F6" />
                </div>
            </CustomDialog>
        );
    }

    const isHourlyLeaveType = Number(formData.leaveType) === 1;

    const inputClassName = "w-full h-10 rounded-lg border border-[#E5E7EB] bg-[#e5e7eb] px-3 text-sm text-[#111827] outline-none";
    const textAreaClassName = "w-full rounded-lg border border-[#E5E7EB] bg-[#e5e7eb] px-3 py-2 text-sm text-[#111827] outline-none resize-none";

    const numberOfDaysOffRaw =
        formData.displayDays ??
        resolveDisplayDays(leaveDetail) ??
        resolveDisplayDays(leaveInfo) ??
        "";
    const numberOfDaysOff =
        numberOfDaysOffRaw === "" || numberOfDaysOffRaw == null
            ? ""
            : formatDecimalNumber(numberOfDaysOffRaw);
    const requestType = String(leaveDetail?.requestType || leaveInfo?.requestType || "").toUpperCase();
    const isDeleteLeaveRequest = requestType === "DELETE_LEAVE";
    const isEditLeaveRequest = requestType === "EDIT_LEAVE";
    const requestStatusId = leaveDetail?.requestStatusId ?? formData.selecStatusLeave;
    const requestTitle = requestType === "EDIT_LEAVE"
        ? t("common.edit-request-leave")
        : isDeleteLeaveRequest
            ? t("common.request-delete-leave")
            : t("common.leave-request-title");

    const oldLeaveData = leaveDetail?.oldData || {};
    const newLeaveData = leaveDetail?.newData || {};
    const oldLeaveTypeId = oldLeaveData.leaveTypeId;
    const newLeaveTypeId = formData.leaveType ?? newLeaveData.leaveTypeId;
    const oldDateRangeText = `${formatRequestDateTime(oldLeaveData.start)}  →  ${formatRequestDateTime(oldLeaveData.end)}`;
    const newDateRangeText = `${formatRequestDateTime(formData.requestedFrom)}  →  ${formatRequestDateTime(formData.requestedTo)}`;
    const oldNumOfDayRaw = resolveDisplayDays(oldLeaveData);
    const oldNumOfDay =
        oldNumOfDayRaw === "" || oldNumOfDayRaw == null
            ? ""
            : formatDecimalNumber(oldNumOfDayRaw);
    const comparisonRowClassName =
        "grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)] gap-3 items-end";
    const comparisonArrow = (
        <div className="hidden md:flex justify-center text-2xl text-[#525252] pb-1.5">→</div>
    );

    return (
        <CustomDialog maxWidth={'md'} open loading={isLoading} onClose={handleCloseDialog} keepMounted>
            <DialogTitle>
                <div className='flex justify-between'>
                    <div className='flex items-center gap-4'>
                        <p className='text-xl font-semibold '>{requestTitle}</p>
                    </div>
                    <GrayIconButton onClick={handleCloseDialog} className='self-start' disabled={isLoading}>
                        <CloseIcon />
                    </GrayIconButton>
                </div>
            </DialogTitle>
            <DialogContent>
                {isRequestMode && (
                    <div className="grid grid-cols-2 border-b border-[#E5E7EB] mb-5">
                        <button
                            type="button"
                            onClick={() => setActiveTab("details")}
                            className={`py-3 text-sm font-semibold border-b-2 ${
                                activeTab === "details"
                                    ? "text-[#0057FF] border-[#0057FF]"
                                    : "text-[#6B7280] border-transparent"
                            }`}
                        >
                            {t("button.detail")}
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("history")}
                            className={`py-3 text-sm font-semibold border-b-2 ${
                                activeTab === "history"
                                    ? "text-[#0057FF] border-[#0057FF]"
                                    : "text-[#6B7280] border-transparent"
                            }`}
                        >
                            {t("common.history")}
                        </button>
                    </div>
                )}

                {!isRequestMode || activeTab === "details" ? (
                    isDeleteLeaveRequest ? (
                        <div className="space-y-3">
                            <div className="rounded-md bg-[#F3F4F6] border-l-[6px] border-[#0057FF] p-4">
                                <div className="flex items-center gap-3 flex-wrap text-sm mb-1">
                                    <span className="font-semibold text-[#111827]">{formData.selectUser || "-"}</span>
                                    <span className="text-[#6B7280]">{t('common.leave-request-title')}</span>
                                    <span
                                        className="font-medium"
                                        style={{ color: getRequestStatusColor(requestStatusId) }}
                                    >
                                        {getRequestStatusLabel(requestStatusId)}
                                    </span>
                                </div>
                                <p className="text-sm font-semibold text-[#111827] mb-1">
                                    {getLeaveTypeLabel(formData.leaveType)}
                                </p>
                                <div className="flex items-center gap-5 text-sm text-[#1F2937] flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <CalendarV2Icon16pxSvg />
                                        <span>
                                            {formatRequestDateTime(formData.requestedFrom)}
                                            {" - "}
                                            {formatRequestDateTime(formData.requestedTo)}
                                        </span>
                                    </div>
                                    {formData.displayDays ? (
                                        <div className="flex items-center gap-2">
                                            <AccessTimeIcon fontSize="small" className='text-[#6B7280]' />
                                            <span>{`${formData.displayDays} ${t('common.days')}`}</span>
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm text-[#111827] mb-2">
                                    {t("common.reason")}
                                </label>
                                <textarea
                                    className={textAreaClassName}
                                    rows={3}
                                    value={formData.requestReason || ""}
                                    readOnly
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-[#111827] mb-2">
                                    {t("common.approve-reject-reason")}
                                </label>
                                <textarea
                                    className={textAreaClassName}
                                    rows={3}
                                    value={formData.approveReason || ""}
                                    readOnly
                                />
                            </div>
                        </div>
                    ) : (
                    <div className={`space-y-3${!isRequestMode ? " pt-4" : ""}`}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm text-[#111827] mb-2">
                                    {t("common.user")}
                                </label>
                                <input
                                    className={inputClassName}
                                    value={formData.selectUser || ""}
                                    readOnly
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-[#111827] mb-2">
                                    {t("common.request-time")}
                                </label>
                                <input
                                    className={inputClassName}
                                    value={formData.createdTime ? formatRequestDateTime(formData.createdTime) : "-"}
                                    readOnly
                                />
                            </div>
                        </div>

                        {isEditLeaveRequest ? (
                            <div className={comparisonRowClassName}>
                                <div>
                                    <label className="block text-sm text-[#111827] mb-2">
                                        {t("common.leave-type-old")}
                                    </label>
                                    <input
                                        className={inputClassName}
                                        value={getLeaveTypeLabel(oldLeaveTypeId)}
                                        readOnly
                                    />
                                </div>
                                {comparisonArrow}
                                <div>
                                    <label className="block text-sm text-[#111827] mb-2">
                                        {t("common.leave-type-new")}
                                    </label>
                                    <input
                                        className={inputClassName}
                                        value={getLeaveTypeLabel(newLeaveTypeId)}
                                        readOnly
                                    />
                                </div>
                            </div>
                        ) : null}

                        <div className={`grid grid-cols-1 ${isHourlyLeaveType || isEditLeaveRequest ? "" : "md:grid-cols-2"} gap-3`}>
                            {!isEditLeaveRequest && (
                                <div>
                                    <label className="block text-sm text-[#111827] mb-2">
                                        {t("common.leave-type")}
                                    </label>
                                    <input
                                        className={inputClassName}
                                        value={getLeaveTypeLabel(formData.leaveType)}
                                        readOnly
                                    />
                                </div>
                            )}
                            {!isHourlyLeaveType && (
                                <div>
                                    <div className="flex items-center gap-1 mb-2">
                                        <label className="block text-sm text-[#111827]">
                                            {t("common.leave-entitlement")}
                                        </label>
                                        {leaveEntitlementInfo.details?.length ? (
                                            <DefaultTooltip
                                                title={t("common.leave-entitlement")}
                                                readMore={false}
                                                content={
                                                    <LeaveUsedDaysTooltipContent
                                                        details={leaveEntitlementInfo.details}
                                                        totalDays={leaveEntitlementInfo.usedDays}
                                                    />
                                                }
                                            >
                                                <QuestionIcon16pxSvg className="fill-[#BDBDBD] shrink-0 w-3.5 h-3.5 cursor-help" />
                                            </DefaultTooltip>
                                        ) : null}
                                    </div>
                                    <input
                                        className={inputClassName}
                                        value={leaveEntitlementInfo.text}
                                        readOnly
                                    />
                                </div>
                            )}
                        </div>

                        {isEditLeaveRequest ? (
                            <>
                                <div className={comparisonRowClassName}>
                                    <div>
                                        <label className="block text-sm text-[#111827] mb-2">
                                            {t("common.date-time-old")}
                                        </label>
                                        <input
                                            className={inputClassName}
                                            value={oldDateRangeText}
                                            readOnly
                                        />
                                    </div>
                                    {comparisonArrow}
                                    <div>
                                        <label className="block text-sm text-[#111827] mb-2">
                                            {t("common.date-time-new")}
                                        </label>
                                        <input
                                            className={inputClassName}
                                            value={newDateRangeText}
                                            readOnly
                                        />
                                    </div>
                                </div>
                                <div className={comparisonRowClassName}>
                                    <div>
                                        <label className="block text-sm text-[#111827] mb-2">
                                            {t("common.no-of-days-old")}
                                        </label>
                                        <input
                                            className={inputClassName}
                                            value={oldNumOfDay}
                                            readOnly
                                        />
                                    </div>
                                    {comparisonArrow}
                                    <div>
                                        <label className="block text-sm text-[#111827] mb-2">
                                            {t("common.no-of-days-new")}
                                        </label>
                                        <input
                                            className={inputClassName}
                                            value={numberOfDaysOff}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm text-[#111827] mb-2">
                                        {t("common.date-time")}
                                    </label>
                                    <input
                                        className={inputClassName}
                                        value={`${formatRequestDateTime(formData.requestedFrom)}  →  ${formatRequestDateTime(formData.requestedTo)}`}
                                        readOnly
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-[#111827] mb-2">
                                        {t("common.no-of-days")}
                                    </label>
                                    <input
                                        className={inputClassName}
                                        value={numberOfDaysOff}
                                        readOnly
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm text-[#111827] mb-2">
                                {t('common.approve-reject-by')}
                            </label>
                            <input
                                className={inputClassName}
                                value={formData.modifiedBy || "-"}
                                readOnly
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-[#111827] mb-2">
                                {t("common.reason")}
                            </label>
                            <textarea
                                className={textAreaClassName}
                                rows={3}
                                value={formData.requestReason || ""}
                                readOnly
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-[#111827] mb-2">
                                {t("common.approve-reject-reason")}
                            </label>
                            <textarea
                                className={textAreaClassName}
                                rows={3}
                                value={formData.approveReason || ""}
                                readOnly
                            />
                        </div>
                    </div>
                    )
                ) : (
                    <div className="mt-4 relative">
                        {!parsedLeaveHistory.length ? (
                            <div className="py-10 text-center text-sm text-[#6B7280]">
                                {t("common.no-data")}
                            </div>
                        ) : (
                            parsedLeaveHistory.map((item, index) => (
                                <div key={item.id ?? index} className="flex gap-4 relative pb-8">
                                    {index !== parsedLeaveHistory.length - 1 && (
                                        <div className="absolute left-[19px] top-10 w-[2px] h-full bg-gray-200" />
                                    )}

                                    <div className="flex flex-col items-center z-10">
                                        <div
                                            className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                                                item.requestStatusName === "APPROVED"
                                                    ? "bg-[#F0FDF4] border-[#E5E7EB]"
                                                    : item.requestStatusName === "REJECTED"
                                                        ? "bg-[#FEF2F2] border-[#E5E7EB]"
                                                        : "bg-[#FEF2E6] border-[#E5E7EB]"
                                            }`}
                                        >
                                            <div
                                                className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                    item.requestStatusName === "APPROVED"
                                                        ? "bg-green-500"
                                                        : item.requestStatusName === "REJECTED"
                                                            ? "bg-red-500"
                                                            : "bg-orange-400"
                                                }`}
                                            >
                                                {item.requestStatusName === "APPROVED" && (
                                                    <DoneIcon sx={{ color: "#fff", fontSize: 16 }} />
                                                )}
                                                {item.requestStatusName === "REJECTED" && (
                                                    <ClearIcon sx={{ color: "#fff", fontSize: 16 }} />
                                                )}
                                                {(item.requestStatusName === "PENDING" ||
                                                    !["APPROVED", "REJECTED"].includes(item.requestStatusName)) && (
                                                    <AccessTimeIcon sx={{ color: "#fff", fontSize: 16 }} />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex-1 bg-white">
                                        <div className="flex items-start justify-between gap-3">
                                            <p className="font-semibold text-gray-900">
                                                {item.actorName}{" "}
                                                <span className="font-normal text-[#1F2937]">
                                                    {item.actionLabel} {item.typeLabel}
                                                </span>
                                            </p>
                                            <p className="text-sm text-[#6B7280] whitespace-nowrap">
                                                {formatRequestDateTime(item.createdAt)}
                                            </p>
                                        </div>

                                        <p className="text-sm text-gray-600 mt-1">
                                            <span className="text-gray-500">{t("common.content-036")}:</span>{" "}
                                            <span className="text-[#1F2937]">{item.timeRangeLabel}</span>
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            <span className="text-gray-500">{t("common.type")}:</span>{" "}
                                            <span className="text-[#1F2937]">{item.leaveTypeLabel}</span>
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            <span className="text-gray-500">{t("common.reason")}:</span>{" "}
                                            <span className="text-[#1F2937]">{item.reasonLabel}</span>
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </DialogContent>
            {openCorfirmApproveRequest && <CorfirmApproveRequestDialog onClose={handleCloseApproveRequest} formData={formData} onCloseDetail={handleCloseDialog} leaveInfo={leaveInfo} onRefresh={onRefresh} onLoadingChange={(loading) => setLoadingCount((prev) => Math.max(prev + (loading ? 1 : -1), 0))} />}
            {openCorfirmRejectRequest && <CorfirmRejectRequestDialog onClose={handleCloseRejectRequest} formData={formData} onCloseDetail={handleCloseDialog} leaveInfo={leaveInfo} onRefresh={onRefresh} onLoadingChange={(loading) => setLoadingCount((prev) => Math.max(prev + (loading ? 1 : -1), 0))} />}
        </CustomDialog>
    )
}
export default DetailRequestDialogAR